'use client'

import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react'

export interface SeasonalThemeConfig {
  id: string
  name: string
  accent: string
  glow: string
  emoji: string
  startMonth: number
  startDay: number
  endMonth: number
  endDay: number
}

const themes: SeasonalThemeConfig[] = [
  { id: 'default', name: 'Padrão', accent: '#7c5cfc', glow: 'rgba(124, 92, 252, 0.4)', emoji: '✨', startMonth: 1, startDay: 1, endMonth: 12, endDay: 31 },
  { id: 'carnaval', name: 'Carnaval', accent: '#ff6b6b', glow: 'rgba(255, 107, 107, 0.4)', emoji: '🎭', startMonth: 2, startDay: 1, endMonth: 2, endDay: 28 },
  { id: 'pascoa', name: 'Páscoa', accent: '#f5d942', glow: 'rgba(245, 217, 66, 0.4)', emoji: '🐰', startMonth: 3, startDay: 1, endMonth: 4, endDay: 30 },
  { id: 'junina', name: 'Festa Junina', accent: '#e84393', glow: 'rgba(232, 67, 147, 0.4)', emoji: '🌽', startMonth: 6, startDay: 1, endMonth: 6, endDay: 30 },
  { id: 'black-friday', name: 'Black Friday', accent: '#000000', glow: 'rgba(255, 215, 0, 0.4)', emoji: '🖤', startMonth: 11, startDay: 20, endMonth: 11, endDay: 30 },
  { id: 'natal', name: 'Natal', accent: '#c62828', glow: 'rgba(198, 40, 40, 0.4)', emoji: '🎄', startMonth: 12, startDay: 1, endMonth: 12, endDay: 25 },
  { id: 'ano-novo', name: 'Ano Novo', accent: '#ffd700', glow: 'rgba(255, 215, 0, 0.5)', emoji: '🎆', startMonth: 12, startDay: 26, endMonth: 1, endDay: 7 },
]

function isDateInRange(month: number, day: number, startM: number, startD: number, endM: number, endD: number): boolean {
  const current = month * 100 + day
  const start = startM * 100 + startD
  const end = endM * 100 + endD

  if (start <= end) {
    return current >= start && current <= end
  }
  return current >= start || current <= end
}

function getCurrentTheme(): SeasonalThemeConfig {
  const now = new Date()
  const month = now.getMonth() + 1
  const day = now.getDate()

  for (const theme of themes) {
    if (theme.id === 'default') continue
    if (isDateInRange(month, day, theme.startMonth, theme.startDay, theme.endMonth, theme.endDay)) {
      return theme
    }
  }
  return themes[0]
}

interface SeasonalThemeContextValue {
  theme: SeasonalThemeConfig
  themes: SeasonalThemeConfig[]
}

const SeasonalThemeContext = createContext<SeasonalThemeContextValue | null>(null)

export function SeasonalThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<SeasonalThemeConfig>(() => getCurrentTheme())

  useEffect(() => {
    const currentTheme = getCurrentTheme()
    setTheme(currentTheme)

    document.documentElement.style.setProperty('--theme-accent', currentTheme.accent)
    document.documentElement.style.setProperty('--theme-accent-glow', currentTheme.glow)
  }, [])

  const value = useMemo(() => ({ theme, themes }), [theme])

  return (
    <SeasonalThemeContext.Provider value={value}>
      {children}
    </SeasonalThemeContext.Provider>
  )
}

export function useSeasonalTheme() {
  const context = useContext(SeasonalThemeContext)
  if (!context) {
    throw new Error('useSeasonalTheme must be used within a SeasonalThemeProvider')
  }
  return context
}

export const SeasonalTheme = {
  Provider: SeasonalThemeProvider,
  useTheme: useSeasonalTheme,
  getCurrent: getCurrentTheme,
}